/**
 * Conversations / threads resource — shared route /api/v1/roster/{entry_id}/threads/{type}
 * Same shape for brand and admin.
 *
 * All BE endpoints use WriteData → { data: T } envelope. Always double-unwrap.
 * Types match internal/threads/dto.go exactly.
 */
import { apiClient } from '@discovr/common';
import type { DataResponse } from '@discovr/common';

/**
 * Client-facing message shape.
 * Matches MessageView in internal/threads/dto.go.
 */
export interface ThreadMessage {
  id: string;
  thread_id: string;
  sender_id: string;
  sender_role: string;
  sender_name: string;
  sender_title: string;
  body: string;
  created_at: string;
}

/**
 * Returned by POST /roster/{entry_id}/threads/{type} (get-or-create).
 * Matches GetOrCreateResponse in internal/threads/dto.go.
 */
export interface ThreadGetOrCreateResponse {
  thread_id: string;
  thread_type: string;
  locked: boolean;
  messages: ThreadMessage[];
}

/**
 * Returned by GET /roster/{entry_id}/threads/{type}/messages.
 * Matches GetMessagesResponse in internal/threads/dto.go.
 */
export interface ThreadMessagesResponse {
  messages: ThreadMessage[];
  next_cursor?: string;
}

const base = (entryId: string, type = 'selection') =>
  `/roster/${encodeURIComponent(entryId)}/threads/${encodeURIComponent(type)}`;

export const conversations = {
  /** Get or create the thread for a roster entry. WriteData → { data: ThreadGetOrCreateResponse } */
  getOrCreate: (entryId: string, type = 'selection'): Promise<ThreadGetOrCreateResponse> =>
    apiClient
      .post<DataResponse<ThreadGetOrCreateResponse>>(base(entryId, type))
      .then((r) => r.data.data),

  /** Fetch all messages in a thread. WriteData → { data: ThreadMessagesResponse } */
  getMessages: (entryId: string, type = 'selection'): Promise<ThreadMessagesResponse> =>
    apiClient
      .get<DataResponse<ThreadMessagesResponse>>(`${base(entryId, type)}/messages`)
      .then((r) => r.data.data),

  /** Send a message to a thread. WriteData(w, 201, MessageView) → { data: ThreadMessage } */
  sendMessage: (entryId: string, text: string, type = 'selection'): Promise<ThreadMessage> =>
    apiClient
      .post<DataResponse<ThreadMessage>>(`${base(entryId, type)}/messages`, { body: text })
      .then((r) => r.data.data),

  /** Mark thread as read. WriteNoContent → 204. */
  markRead: (entryId: string, type = 'selection', messageId?: string): Promise<void> =>
    apiClient
      .post(`${base(entryId, type)}/read`, messageId ? { last_read_message_id: messageId } : undefined)
      .then(() => undefined),
};
